import * as L from 'leaflet';
import { IGeolocation } from '../../shared/interfaces/IGeolocation';

interface ClusterLike {
    getChildCount: () => number;
}

export interface MarkerClusterOptions {
    disableClusteringAtZoom: number;
    showCoverageOnHover: boolean;
    iconCreateFunction: (cluster: ClusterLike) => L.DivIcon;
}

export const MAP_OPTIONS: L.MapOptions = {
    attributionControl: true,
    layers: [
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        })
    ],
    zoom: 2,
    center: L.latLng(37.297979, 7.137493)
};

export const MARKER_CLUSTER_OPTIONS: MarkerClusterOptions = {
    disableClusteringAtZoom: 17,
    showCoverageOnHover: false,
    iconCreateFunction: createMarkerClusterIcon
};

/**
 * Builds a Leaflet DivIcon for a marker cluster, choosing size and CSS class
 * based on the number of child markers (small < 10, medium < 100, large otherwise).
 *
 * @param cluster - The cluster whose child count drives the icon appearance.
 * @returns A styled `L.DivIcon` displaying the child count.
 */
export function createMarkerClusterIcon(cluster: ClusterLike): L.DivIcon {
    const childCount = cluster.getChildCount();

    let clusterClass = ' marker-cluster-';
    let size = 40;
    if (childCount < 10) {
        clusterClass += 'small';
        size = 20;
    } else if (childCount < 100) {
        clusterClass += 'medium';
    } else {
        clusterClass += 'large';
        size = 60;
    }

    return new L.DivIcon({
        html: `<div><span>${childCount}</span></div>`,
        className: `marker-cluster${clusterClass}`,
        iconSize: new L.Point(size, size)
    });
}

/**
 * Normalizes a raw coordinate value by mapping `null`, `undefined`, and `NaN` to `null`.
 *
 * @param value - The coordinate value to normalize.
 * @returns The original numeric value, or `null` if it is missing or `NaN`.
 */
export function normalizeCoordinate(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
        return null;
    }

    return Number.isNaN(value) ? null : value;
}

/**
 * Counts how often each `latitude|longitude` pair occurs across the given locations.
 * Locations with missing or invalid coordinates are skipped.
 *
 * @param locations - The geolocations to inspect.
 * @returns A map from a `"lat|lng"` key to the number of occurrences.
 */
export function buildCoordinateCount(locations: IGeolocation[]): Map<string, number> {
    const coordinatesCount = new Map<string, number>();

    locations.forEach(location => {
        const lat = normalizeCoordinate(location.latitude);
        const lng = normalizeCoordinate(location.longitude);
        if (lat === null || lng === null) {
            return;
        }

        const key = `${lat}|${lng}`;
        coordinatesCount.set(key, (coordinatesCount.get(key) ?? 0) + 1);
    });

    return coordinatesCount;
}

/**
 * Returns the coordinates to use when rendering a marker for the given location.
 * If multiple locations share the same coordinates, a tiny random jitter is applied
 * to visually separate the overlapping markers.
 *
 * @param location - The location to place on the map.
 * @param coordinatesCount - Map of coordinate occurrences produced by {@link buildCoordinateCount}.
 * @returns The final `{ lat, lng }` for the marker, or `null` if the location has no valid coordinates.
 */
export function getFinalMarkerCoordinates(
    location: IGeolocation,
    coordinatesCount: Map<string, number>
): { lat: number; lng: number } | null {
    const lat = normalizeCoordinate(location.latitude);
    const lng = normalizeCoordinate(location.longitude);
    if (lat === null || lng === null) {
        return null;
    }

    const key = `${lat}|${lng}`;
    const hasDuplicates = (coordinatesCount.get(key) ?? 0) > 1;

    return {
        lat: hasDuplicates ? lat + (Math.random() - 0.5) / 10000 : lat,
        lng: hasDuplicates ? lng + (Math.random() - 0.5) / 10000 : lng
    };
}

/**
 * Finds the location whose normalized coordinates exactly match the given marker's position.
 * Note: this matches against un-jittered coordinates and will not resolve markers that were
 * offset by {@link getFinalMarkerCoordinates} to disambiguate duplicates.
 *
 * @param locations - Candidate locations to search.
 * @param marker - The Leaflet marker whose position is used for the lookup.
 * @returns The matching `IGeolocation`, or `undefined` if no location matches.
 */
export function findLocationByMarkerCoordinates(locations: IGeolocation[], marker: L.Marker): IGeolocation | undefined {
    const markerLat = marker.getLatLng().lat;
    const markerLng = marker.getLatLng().lng;

    return locations.find(location => {
        const lat = normalizeCoordinate(location.latitude);
        const lng = normalizeCoordinate(location.longitude);
        return lat === markerLat && lng === markerLng;
    });
}
